import React from "react";

import { TimeUnit } from "./time-unit";
import { useIntl } from "react-intl";

import styles from "./styles.modules.scss";

const getTimeLeft = (timeInMilliseconds: number) => {
    const totalSeconds =
        (timeInMilliseconds > 0 ? timeInMilliseconds : 0) / 1000;
    const totalMinutes = totalSeconds / 60;
    const totalHours = totalMinutes / 60;
    const totalDays = totalHours / 24;

    return {
        totalSeconds: Math.floor(totalSeconds),
        seconds: Math.floor(totalSeconds % 60),

        totalMinutes: Math.max(1, Math.floor(totalMinutes)),
        minutes: Math.floor(totalMinutes % 60),

        totalHours: Math.floor(totalHours),
        hours: Math.floor(totalHours % 24),

        totalDays: Math.floor(totalDays),
        days: Math.floor(totalDays),
    };
};

export const Timer = ({
    countdown,
    timerLabel,
    format,
}: {
    countdown: number;
    timerLabel?: string;
    format: "total-hours" | "three-values";
}) => {
    const { formatMessage } = useIntl();

    const {
        days,
        hours,
        minutes,
        seconds,
        totalDays,
        totalHours,
        totalMinutes,
        totalSeconds,
    } = getTimeLeft(countdown);

    const daysFormatted =
        format === "three-values" && days > 0
            ? {
                  value: days,
                  unit: formatMessage({
                      id: "globalText.langDaysAbbreviated",
                  }),
                  total: totalDays,
              }
            : undefined;

    const hoursFormatted = {
        value: format === "total-hours" ? totalHours : hours,
        unit: formatMessage({
            id: "globalText.langHoursAbbreviated",
        }),
        total: totalHours,
    };

    const minutesFormatted = {
        value: minutes,
        unit: formatMessage({
            id: "globalText.langMinutesAbbreviated",
        }),
        total: totalMinutes,
    };

    const secondsFormatted =
        format === "three-values" && days === 0
            ? {
                  value: seconds,
                  unit: formatMessage({
                      id: "globalText.langSecondsAbbreviated",
                  }),
                  total: totalSeconds,
              }
            : undefined;

    return (
        <div className={`timer ${styles.timer}`}>
            {timerLabel && (
                <div className={`label ${styles.label}`}>{timerLabel}</div>
            )}

            <div className={`counter ${styles.counter}`}>
                {daysFormatted && (
                    <div
                        className={`days ${styles.days}`}
                        data-total={daysFormatted.total}
                        data-value={daysFormatted.value}
                    >
                        <TimeUnit timeUnit={daysFormatted} />
                    </div>
                )}

                <div
                    className={`hours ${styles.hours}`}
                    data-total={hoursFormatted.total}
                    data-value={hoursFormatted.value}
                >
                    <TimeUnit timeUnit={hoursFormatted} />
                </div>

                <div
                    className={`minutes ${styles.minutes}`}
                    data-total={minutesFormatted.total}
                    data-value={minutesFormatted.value}
                >
                    <TimeUnit timeUnit={minutesFormatted} />
                </div>

                {secondsFormatted && (
                    <div
                        className={`seconds ${styles.seconds}`}
                        data-total={secondsFormatted.total}
                        data-value={secondsFormatted.value}
                    >
                        <TimeUnit timeUnit={secondsFormatted} />
                    </div>
                )}
            </div>
        </div>
    );
};
